import { Alert, Box, Button, OutlinedInput, Typography } from "@mui/material";
import { ChangeEventHandler, FormEventHandler, useState } from "react";

import { twoFactorAuthCodeLength } from "@dfp/isomorphic-constants";
import { useCommonTranslation } from "@dfp/react-common-translation";
import { AppDialogActions, AppDialogContent } from "@dfp/react-components";
import { useAsyncCallback } from "@dfp/react-utils";

import {
  isAuthRequestedError,
  isPlatformRequestError,
} from "../errors/index.ts";
import { RequestAuthorization } from "../types/requestAuthorization.ts";
import { useTT } from "../useTT.ts";
import { UserFriendlyId } from "./UserFriendlyId.tsx";
import { useTranslateAuthError } from "./useTranslateAuthError.ts";

type Props = RequestAuthorization;

export function CodeInputAuthDialog({
  errorAttributes: {
    authPersonName,
    phoneNumberShaded,
    requestId,
    authMethod,
    emailShaded,
    userFriendlyId,
  },
  onAuthError,
  onAuthorize,
}: Props) {
  const [authCode, setAuthCode] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { tt } = useTT();
  const { commonTT } = useCommonTranslation();

  const translateAuthError = useTranslateAuthError();

  const onFieldChange: ChangeEventHandler<HTMLInputElement> = (ev) => {
    setAuthCode(ev.target.value);
    setErrorMessage(null);
  };

  const { callback: handleAuthorizeSubmit, isLoading: isSubmitting } =
    useAsyncCallback(async () => {
      if (authCode === "") {
        setErrorMessage(tt("dialog.Errors.fieldEmpty"));
        return;
      }

      setErrorMessage(null);

      try {
        await onAuthorize({
          authCode,
          authRequestId: requestId,
        });
      } catch (err) {
        if (isPlatformRequestError(err, 401, "RequestAuthorizationFailed")) {
          setErrorMessage(translateAuthError(err));
        } else {
          onAuthError(err);
        }
      } finally {
        setAuthCode("");
      }
    });

  const onSubmit: FormEventHandler = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    await handleAuthorizeSubmit();
  };

  const { callback: onClickResend, isLoading: isResendingCode } =
    useAsyncCallback(async () => {
      setErrorMessage(null);
      try {
        await onAuthorize();
      } catch (err) {
        if (!isAuthRequestedError(err)) {
          setErrorMessage(tt("dialog.Errors.authMethodNotConfigured"));
        }
      }
    });

  const dialogContentText = () => {
    if (authMethod === "Email") {
      return authPersonName
        ? tt("dialog.OnBehalfOf.textSentEmail", {
            email: emailShaded ?? "",
            name: authPersonName,
          })
        : tt("dialog.Generic.textSentEmail", {
            email: emailShaded ?? "",
          });
    } else {
      return authPersonName
        ? tt("dialog.OnBehalfOf.textSentSMS", {
            phone: phoneNumberShaded ?? "",
            name: authPersonName,
          })
        : tt("dialog.Generic.textSentSMS", {
            phone: phoneNumberShaded ?? "",
          });
    }
  };

  return (
    <>
      <form onSubmit={onSubmit}>
        <AppDialogContent>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography>{dialogContentText()}</Typography>
            <OutlinedInput
              name="authorizationcode"
              value={authCode}
              onChange={onFieldChange}
              type="text"
              fullWidth
              // autofocus the field, so it's easier to enter the code
              autoFocus
              // Apple autocomplete from connected iPhone
              autoComplete="one-time-code"
              inputProps={{
                maxLength: twoFactorAuthCodeLength,
              }}
            />
            <UserFriendlyId userFriendlyId={userFriendlyId} />
          </Box>
        </AppDialogContent>

        <AppDialogActions>
          <Button
            color="secondary"
            onClick={onClickResend}
            loading={isResendingCode}
          >
            {tt("dialog.btnResend")}
          </Button>
          <div style={{ flex: 1 }} />
          <Button color="primary" type="submit" loading={isSubmitting}>
            {commonTT("Button.confirm")}
          </Button>
        </AppDialogActions>
      </form>
      {errorMessage ? (
        <Box sx={{ mx: 4, mb: 4 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      ) : null}
    </>
  );
}
